import React from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { Box, Chip, Link, MenuItem, TextField, Tooltip, Typography } from '@mui/material';
import useExpenseLookups from '../../components/expense/useExpenseLookups';
import { formatDate, STATUS_META } from '../../services/expenseVouchersApi';
import { ADVANCE_STATE_META, formatMoney, reportsApi, SETTLEMENT_RESULT_META } from '../../services/expenseAccountsApi';
import {
  PaymentStatusChip, useAccountsBase, useApprovalsBase, VoucherStatusChip,
} from '../../components/expense/voucherShared';
import { DateRangeFilter, EmployeeFilter } from '../../components/expense/AccountsFilters';
import {
  CurrencyTotals, ExportButton, FilterBar, ReportsHeader, ReportTable, useReport, useReportsBase, WalletPositions, walletLedgerLink,
} from '../../components/expense/ReportShared';

const EMPTY_FILTERS = { employeeId: '', status: '', state: '', currency: '', submittedFrom: '', submittedTo: '', approvedFrom: '', approvedTo: '' };

export const AdvanceStateChip = ({ state }) => {
  const meta = ADVANCE_STATE_META[state] || { label: state, color: 'default' };
  const chip = <Chip size="small" label={meta.label} color={meta.color} />;
  return meta.hint ? <Tooltip title={meta.hint}>{chip}</Tooltip> : chip;
};

// Accounts Advances & Settlements: every advance with its active settlement and the Phase 4
// advance-vs-settlement result. Draft / rejected / cancelled advances are never unsettled.
// The advance-vs-settlement figures are EXPECTED differences, not cash: an Advance only
// credits the top-up the wallet needed (Phase 5B-1A). The actual money position is the
// employee's wallet balance (linked to the wallet ledger).
const AccountsAdvancesReport = () => {
  const navigate = useNavigate();
  const accountsBase = useAccountsBase();
  const approvalsBase = useApprovalsBase();
  const reportsBase = useReportsBase();
  const { currencies, users } = useExpenseLookups(['currencies', 'users']);
  const report = useReport(reportsApi.advances, { emptyFilters: EMPTY_FILTERS, defaultSort: { sort: 'voucherDate', order: 'desc' } });
  const { filters, setFilter, setFilterValue } = report;
  const money = (row, field) => formatMoney(row[field], row.currency);

  const open = (r) => {
    if (r.advanceStatus === 'APPROVED') navigate(`${accountsBase}/${r._id}`);
    else if (r.advanceStatus !== 'DRAFT') navigate(`${approvalsBase}/${r._id}`);
  };

  const columns = [
    { label: 'Voucher No.', sortKey: 'voucherNumber', nowrap: true, render: (r) => <Typography variant="body2" sx={{ fontWeight: 600 }}>{r.voucherNumber}</Typography> },
    { label: 'Voucher Date', sortKey: 'voucherDate', nowrap: true, render: (r) => formatDate(r.voucherDate) },
    { label: 'Employee', sortKey: 'employee', render: (r) => r.employeeName },
    { label: 'Currency', nowrap: true, render: (r) => r.currency || '-' },
    { label: 'Advance Requested', nowrap: true, align: 'right', render: (r) => money(r, 'advanceAmount') },
    {
      label: 'Credited to Wallet',
      nowrap: true,
      align: 'right',
      render: (r) => (r.creditedAmount === null ? <Typography variant="caption" color="text.secondary">Not paid yet</Typography> : (
        <>{money(r, 'creditedAmount')}{r.creditReversed && <Typography variant="caption" color="error" sx={{ display: 'block' }}>reversed</Typography>}</>
      )),
    },
    {
      label: 'Advance Status',
      render: (r) => (
        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
          <VoucherStatusChip status={r.advanceStatus} />
          {r.advancePaymentStatus && <PaymentStatusChip status={r.advancePaymentStatus} />}
        </Box>
      ),
    },
    { label: 'Settlement State', sortKey: 'state', render: (r) => <AdvanceStateChip state={r.state} /> },
    {
      label: 'Settlement',
      render: (r) => (r.settlement ? (
        <>{r.settlement.voucherNumber}<Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>{STATUS_META[r.settlement.status]?.label || r.settlement.status}</Typography></>
      ) : '-'),
    },
    { label: 'Settlement Amount', nowrap: true, align: 'right', render: (r) => (r.settlementAmount === null ? '-' : money(r, 'settlementAmount')) },
    { label: 'Result', render: (r) => (r.settlementResult ? <Chip size="small" variant="outlined" label={SETTLEMENT_RESULT_META[r.settlementResult]?.label || r.settlementResult} /> : '-') },
    { label: 'Expected Recoverable', nowrap: true, align: 'right', render: (r) => (r.expectedRecoverable === null ? '-' : money(r, 'expectedRecoverable')) },
    { label: 'Expected Additional Payable', nowrap: true, align: 'right', render: (r) => (r.expectedAdditionalPayable === null ? '-' : money(r, 'expectedAdditionalPayable')) },
    { label: 'Settlement Date', nowrap: true, render: (r) => formatDate(r.settlementDate) },
    {
      label: 'Employee Wallet (actual)',
      nowrap: true,
      align: 'right',
      render: (r) => (r.walletBalance === null || !r.currency ? '-' : (
        <Link component={RouterLink} to={walletLedgerLink(reportsBase, r.employee, r.currency)} onClick={(e) => e.stopPropagation()}
          sx={{ fontWeight: 600, color: r.walletBalance < 0 ? 'error.main' : undefined }}>
          {money(r, 'walletBalance')}
        </Link>
      )),
    },
  ];

  return (
    <Box sx={{ bgcolor: 'background.default', p: 3 }}>
      <ReportsHeader
        title="Advances & Settlements"
        subtitle="Advances, their active settlement and the expected difference. Expected amounts are not cash: the actual position is the employee's wallet (positive = recoverable, negative = payable). Draft, rejected and cancelled advances are never unsettled."
        actions={<ExportButton onExport={reportsApi.exportAdvances} params={report.params} />}
      />

      <FilterBar onClear={report.clear}>
        <TextField size="small" placeholder="Search voucher, employee..." value={report.search} onChange={(e) => report.setSearch(e.target.value)} sx={{ minWidth: 240 }} />
        <EmployeeFilter users={users} value={filters.employeeId} onChange={setFilterValue('employeeId')} />
        <TextField select size="small" label="Advance status" value={filters.status} onChange={setFilter('status')} sx={{ minWidth: 170 }}>
          <MenuItem value="">All</MenuItem>
          {Object.entries(STATUS_META).map(([value, m]) => <MenuItem key={value} value={value}>{m.label}</MenuItem>)}
        </TextField>
        <TextField select size="small" label="Settlement state" value={filters.state} onChange={setFilter('state')} sx={{ minWidth: 190 }}>
          <MenuItem value="">All</MenuItem>
          {Object.entries(ADVANCE_STATE_META).map(([value, m]) => <MenuItem key={value} value={value}>{m.label}</MenuItem>)}
        </TextField>
        <TextField select size="small" label="Currency" value={filters.currency} onChange={setFilter('currency')} sx={{ minWidth: 130 }}>
          <MenuItem value="">All</MenuItem>
          {currencies.map((c) => <MenuItem key={c.value} value={c.value}>{c.value}</MenuItem>)}
        </TextField>
        <DateRangeFilter label="Submitted" from={filters.submittedFrom} to={filters.submittedTo}
          onFromChange={setFilterValue('submittedFrom')} onToChange={setFilterValue('submittedTo')} />
        <DateRangeFilter label="Approved" from={filters.approvedFrom} to={filters.approvedTo}
          onFromChange={setFilterValue('approvedFrom')} onToChange={setFilterValue('approvedTo')} />
      </FilterBar>

      <CurrencyTotals
        testId="advances-totals"
        summary={report.summary}
        title={(s) => `${s.currency} - ${s.count} advance${s.count === 1 ? '' : 's'}`}
        lines={(s) => [
          `Requested ${formatMoney(s.advance, s.currency)} - Credited ${formatMoney(s.credited, s.currency)} - Settled ${formatMoney(s.settlement, s.currency)}`,
          `Expected recoverable ${formatMoney(s.expectedRecoverable, s.currency)}`,
          s.expectedAdditionalPayable ? `Expected additional payable ${formatMoney(s.expectedAdditionalPayable, s.currency)}` : null,
          s.states?.NOT_APPLICABLE ? `${s.states.NOT_APPLICABLE} draft / rejected / cancelled (not in totals)` : null,
        ]}
      />

      <WalletPositions positions={report.response?.walletPositions} />

      <ReportTable report={report} columns={columns} emptyText="No advances match." onRowClick={open} />
    </Box>
  );
};

export default AccountsAdvancesReport;
